import type { Photo } from '@/types/listing';

/**
 * Session memory of the photos we have already seen for a listing.
 *
 * A listing reaches the UI from several payloads that disagree about photos,
 * and the difference is invisible in the type:
 *
 *  - `POST /search` (full) — up to `SearchResultPhotoCap` (8) photos.
 *  - `POST /search` with **`light: true`** — the projection the map uses to pull
 *    500 matches cheaply. It omits photos *by design*: every listing comes back
 *    `photos: []` next to `photoCount: 23`.
 *  - `GET /listings/{id}` — the complete set (23, 39, 51…).
 *  - Meilisearch also lags the database on freshly ingested homes, so even the
 *    full search returns `photos: []` with `photoCount: 23` for the newest
 *    listings — the ones at the top of the default sort.
 *
 * So "this listing has no photos" is a claim only the *photo-ful* payloads can
 * make honestly. A photoless record is usually a projection, not a fact, and it
 * must never erase a photo set the session has already been shown: the search
 * page server-renders backfilled photos, and the client refetch (filter change,
 * pagination, a 30-second-stale window, a map pan) returns the raw payload,
 * which blanked the cards that had just painted.
 *
 * Deliberately *not* React Query state: it is keyed by listing id rather than by
 * query, which is the whole point — the same home appears under many query keys
 * (search page, city page, favorites, similar homes, map result set) and the
 * photos belong to the home, not to the query that happened to mention it.
 */

/**
 * Bound on remembered listings. A long browsing session touches a few hundred
 * homes; 8 photos of ~120 bytes each puts the ceiling around 1 MB, and eviction
 * is oldest-first (Map preserves insertion order).
 */
const MAX_ENTRIES = 600;

const remembered = new Map<string, Photo[]>();

/**
 * Record a photo set for a listing. Empty sets are ignored — they are the claim
 * we do not trust. A non-empty set always replaces an older one: a fresher
 * payload may have swapped raw MLS CDN URLs for GCS derivatives, or the MLS may
 * have rotated the media paths, and serving remembered URLs after that would
 * pin 404s for the rest of the session.
 */
export function rememberPhotos(id: string | undefined, photos: Photo[] | undefined): void {
  if (!id || !photos?.length) return;
  // Re-insert so recently seen listings sort last and survive eviction.
  remembered.delete(id);
  remembered.set(id, photos);
  if (remembered.size > MAX_ENTRIES) {
    const oldest = remembered.keys().next().value;
    if (oldest !== undefined) remembered.delete(oldest);
  }
}

/** Photos already seen for this listing, or `undefined` if we have never had any. */
export function recallPhotos(id: string | undefined): Photo[] | undefined {
  return id ? remembered.get(id) : undefined;
}

/** Test seam — the memory is module state and outlives a single render tree. */
export function clearPhotoMemory(): void {
  remembered.clear();
}
